import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { DataGrid, GridColDef, GridToolbar } from '@mui/x-data-grid';
import styled from 'styled-components';
import { COLORS } from '../Styles/Colors';
import { IoIosPin } from 'react-icons/io';
import { Company, CompanyFormInput, Report, USER_ROLE } from '../Types';
import useGetData from '../Hooks/useGetData';
import { AddButton, CancelButton, CloseButton, DeleteButton, DeleteConfirmButton, EditButton, ErrorMessage, GridContainer, LoadingMessage, Message, ModalContent, ModalHeader, ModalOverlay, PageContainer, RecordCount, SubmitButton, Title } from '../Styles/AppStyles';
import Cookies from 'js-cookie';
import { decode, decodeToken } from '../Services/DecodeToken';
import { useSnackbar } from '../Context/SnackbarProvider';
import { ColumnDef, DataTable } from '../Components/Table/DataTable';
import { Header } from '../Components/Header/Header';
import { formatUTC } from '../Services/FormatUtc';
import ReportsIcon from '../Components/Icons/ReportsIcon';
import dayjs from 'dayjs';
import { Box, Button, MenuItem, Select, TextField, Typography, InputAdornment } from '@mui/material';
import useGetCustomData from '../Hooks/useGetCustomData';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DateRangePicker } from '../Components/Date/Datepicker';
import apiClient from '../Services/ApiClient';
import { Search as SearchIcon, Clear as ClearIcon } from '@mui/icons-material';
import { SearchableHeader } from '../Components/Search/SearchableHeader';
import { useColumnSearches } from '../Hooks/useColumnSearch';
import { LocationSelect } from '../Components/Select/LocationSelect';

const TypedIoIosPin = IoIosPin as unknown as React.FC<{ size?: number | string }>;


const TransactionAuditReport: React.FC = () => {
  const token: any = decode(); 
  const id = token.userId;
  
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);





  
  const [ovs] = useGetCustomData('/api/location/user/ovs');
  // Empty string means "All Locations" (LocationSelect setDefault={true}).
  const [selectedOvs, setSelectedOvs] = useState<string>('');

  const [sortField, setSortField] = useState<string>('');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const searchFields = [
  'UserName',
  'ProductName',
  'Product_code',
  'P21OrderNumber',
  'P21InvoiceNumber',
  'VendingLocationName',
  'CustomerName',
  'Note',
  'Key1CD',
  'Key1Value',
  'Key2CD',
  'Key2Value',
  'Key3CD',
  'Key3Value',
];


const {
  columnSearches,
  setSearch,
  clearSearch,
  clearAllSearches,
} = useColumnSearches(searchFields);



  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);

  const baseUrl = useMemo(() => {
    const params = new URLSearchParams();
  
    if (startDate) {
      params.set('startDate', dayjs(startDate).format('YYYY-MM-DD'));
    }
  
    if (endDate) {
      params.set('endDate', dayjs(endDate).format('YYYY-MM-DD'));
    }

    if (selectedOvs) {
      params.set('ovid', selectedOvs);
    }

    Object.entries(columnSearches).forEach(([field, value]) => {
      if (value.trim()) {
        params.set(`${field}`, value.trim());
      }
    });

    if (sortField) {
      params.set('sort_field', sortField);
      params.set('sort_order', sortOrder);
    }
  
    return `/api/reports/transaction-audit?${params.toString()}`;
  }, [startDate, endDate, selectedOvs, columnSearches, sortField, sortOrder]);

  const paginatedUrl = `${baseUrl}&limit=${pageSize}&offset=${page * pageSize}`;
  const [reports, loading, errorFetchReport, refetch] = useGetCustomData(paginatedUrl);

  const [displayedReports, setDisplayedReports] = useState<any[]>([]);


   useEffect(() => {
  if (reports?.data) {
    const formatted = reports.data.map((report: any) => ({
      ...report,
      Created_At: formatUTC(report.Created_At),
      ModifiedDate: formatUTC(report.ModifiedDate),
    }));
    setDisplayedReports(formatted);
  }
}, [reports]);


  const handleFilter = () => {
    setPage(0); 
  };

  const handleColumnSearch = (field: string, value: string) => {
    setSearch(field, value);
    setPage(0); 
  };

  const handleClearColumnSearch = (field: string) => {
    clearSearch(field)
    setPage(0);
  };

  const handleSortChange = (field: string, order: 'asc' | 'desc' | null) => {
    setSortField(order ? field : '');
    setSortOrder(order ?? 'desc');
    setPage(0);
  };

  const handleLocationChange = (value: any) => {
    setSelectedOvs(value === '' || value === null || value === undefined ? '' : String(value));
    setPage(0);
  };

 

  const getActiveSearchCount = () => {
    return Object.values(columnSearches).filter(value => value.trim() !== '').length;
  };

 


  const totalRows = reports?.totalCount ?? 0;

  const {showSnackbar} = useSnackbar();
  const [editedCells, setEditedCells] = useState<Record<string, Record<string, number | ''>>>({});
  const [activeEdit, setActiveEdit] = useState<{id: string, field: string} | null>(null);

  useEffect(() => {
    const handleClickOutside = () => {
      if (activeEdit) {
        setActiveEdit(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [activeEdit]);
  

  const handleCancelChanges = () => {
    setEditedCells({});
    setActiveEdit(null);
  };

  const hasEdits = Object.keys(editedCells).length > 0;

  const columns: ColumnDef<Report>[] = [
    {
      field: 'CustomerName',
      headerName: 'Account',
      width: 180,
      sortable: true,
      renderHeader: () => (
        <SearchableHeader
          field="CustomerName"
          headerName="Account"
          searchValue={columnSearches.CustomerName}
          onSearchChange={handleColumnSearch}
          onClearSearch={handleClearColumnSearch}
        />
      ),
      renderCell: (params: any) => params.value ?? '—',
    },
    {
      field: 'VendingLocationName',
      headerName: 'Location',
      width: 200,
      sortable: false,
      renderHeader: () => (
        <SearchableHeader
          field="VendingLocationName"
          headerName="Location"
          searchValue={columnSearches.VendingLocationName}
          onSearchChange={handleColumnSearch}
          onClearSearch={handleClearColumnSearch}
        />
      ),
      renderCell: (params: any) => params.value ?? '—',
    },
        {
      field: 'ProductName',
      headerName: 'Description',
      width: 150,
      sortable: false,
      searchable: true,
      renderHeader: () => (
        <SearchableHeader
          field="ProductName"
          headerName="Description"
          searchValue={columnSearches.ProductName}
          onSearchChange={handleColumnSearch}
          onClearSearch={handleClearColumnSearch}
        />
      )
    },
    {
      field: 'Product_code',
      headerName: 'Part No.',
      width: 170,
      sortable: false,
      searchable: true,
      renderHeader: () => (
        <SearchableHeader
          field="Product_code"
          headerName="Part No."
          searchValue={columnSearches.Product_code}
          onSearchChange={handleColumnSearch}
          onClearSearch={handleClearColumnSearch}
        />
      )
    },
    {
      field: 'UserName',
      headerName: 'User',
      width: 150,
      sortable: false,
      searchable: true,
      renderHeader: () => (
        <SearchableHeader
          field="UserName"
          headerName="User"
          searchValue={columnSearches.UserName}
          onSearchChange={handleColumnSearch}
          onClearSearch={handleClearColumnSearch}
        />
      )
    },

    { field: 'PreviousQty', headerName: 'Previous Qty', width: 130, sortable: false, renderCell: (params) => renderQuantityCell(params, 'PreviousQty')  },
    { field: 'UpdatedQty', headerName: 'Updated Qty', width: 130, sortable: false, renderCell: (params) => renderQuantityCell(params, 'UpdatedQty') },
    { field: 'Created_At', headerName: 'Created At (UTC)', width: 180, sortable: false },
    {
      field: 'Note',
      headerName: 'Note',
      width: 220,
      sortable: false,
      renderHeader: () => (
        <SearchableHeader
          field="Note"
          headerName="Note"
          searchValue={columnSearches.Note}
          onSearchChange={handleColumnSearch}
          onClearSearch={handleClearColumnSearch}
        />
      ),
    },
    { field: 'NotSyncOrderQty', headerName: 'Not Synced Order Qty', width: 180, sortable: false },
    {
      field: 'P21OrderNumber',
      headerName: 'P21 Order No.',
      width: 180,
      sortable: false,
      renderHeader: () => (
        <SearchableHeader
          field="P21OrderNumber"
          headerName="P21 Order No."
          searchValue={columnSearches.P21OrderNumber}
          onSearchChange={handleColumnSearch}
          onClearSearch={handleClearColumnSearch}
        />
      ),
    },
    {
      field: 'P21InvoiceNumber',
      headerName: 'P21 Invoice No.',
      width: 180,
      sortable: false,
      renderHeader: () => (
        <SearchableHeader
          field="P21InvoiceNumber"
          headerName="P21 Invoice No."
          searchValue={columnSearches.P21InvoiceNumber}
          onSearchChange={handleColumnSearch}
          onClearSearch={handleClearColumnSearch}
        />
      ),
    },
    { 
      field: 'Key1CD', 
      headerName: 'Key 1 Code', 
      width: 140, 
      sortable: false,
      searchable: true,
     
    },
    { 
      field: 'Key1Value', 
      headerName: 'Key 1 Value', 
      width: 140, 
      sortable: false,
      searchable: true,
     
    },
    { 
      field: 'Key2CD', 
      headerName: 'Key 2 Code', 
      width: 140, 
      sortable: false,
      searchable: true,
     
    },
    { 
      field: 'Key2Value', 
      headerName: 'Key 2 Value', 
      width: 140, 
      sortable: false,
      searchable: true,
     
    },
    { 
      field: 'Key3CD', 
      headerName: 'Key 3 Code', 
      width: 140, 
      sortable: false,
      searchable: true,
    
    },
    { 
      field: 'Key3Value', 
      headerName: 'Key 3 Value', 
      width: 140, 
      sortable: false,
      searchable: true,
     
    },
  ];



  const handleQuantityChange = (id: string, field: string, value: number | '') => {
  setEditedCells(prev => ({
    ...prev,
    [id]: {
      ...(prev[id] || {}),
      [field]: value,
    },
  }));
};

  const renderQuantityCell = useCallback((params: any, field: string) => {


    const isEdited = editedCells[params.id]?.[field] !== undefined;
    const isActive = activeEdit?.id === params.id && activeEdit?.field === field;
    const displayValue = isEdited ? editedCells[params.id][field] : params.value;
  
    const handleCellClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      setActiveEdit({ id: params.id, field });
    };
  
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          height: '100%',
          padding: '0 8px',
          backgroundColor: isEdited ? `${COLORS.teal}15` : 'transparent',
          position: 'relative',
        }}
        onClick={handleCellClick}
      >
        {isActive ? (
          <input
            type="number"
            value={displayValue === '' ? '' : String(displayValue)}
            onChange={(e) => {
              const raw = e.target.value;
              const parsed = raw === '' ? '' : Number(raw);
              handleQuantityChange(params.id, field, parsed);
            }}
            disabled={token.roles.level < 50}
            autoFocus
            style={{
              width: '100%',
              borderRadius: '4px',
              padding: '4px',
              outline: 'none',
              border: 'none',
              backgroundColor: 'white',
            }}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === 'Escape') {
                setActiveEdit(null);
              }
            }}
          />
        ) : (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              width: '100%',
              cursor: 'pointer',
            }}
          >
            <span
              style={{
                fontWeight: isEdited ? 'bold' : 'normal',
                color: isEdited ? COLORS.teal : 'inherit',
              }}
            >
              {displayValue === '' ? '-' : displayValue}
            </span>
            {isEdited && (
              <div
                style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  backgroundColor: COLORS.teal,
                  marginLeft: '8px',
                }}
              />
            )}
          </div>
        )}
      </div>
    );
  }, [editedCells, activeEdit, token.roles.level]);


  const handleSaveChanges = async () => {
  const fullPayload = [];

  for (const [id, fields] of Object.entries(editedCells)) {
    const original = displayedReports?.find((item: any) => item.ID == id);

    const mergedFields = {
      UpdatedQty: fields.UpdatedQty ?? original?.UpdatedQty,
      PreviousQty: fields.PreviousQty ?? original?.PreviousQty,
    };

    fullPayload.push({
      id: Number(id),
      ...mergedFields,
    });
  }



  const response = await apiClient('/api/reports/edit/bulk', {
    method: 'PUT',
    body: JSON.stringify(fullPayload),
  });

  if (!response.ok) {
    const errorData = await response.json();
    showSnackbar("Failed to save", "error", "Something went wrong!");
    return;
  }

  const updatedData = displayedReports.map((item: any) => {
      const edits = editedCells[item.id];
      if (!edits) return item;
  
      return {
        ...item,
        ...edits,
      };
    });
  
    setDisplayedReports(updatedData);


    showSnackbar("Changes saved sucessfully", "success");
    setEditedCells({});
    setActiveEdit(null);

};


  return (
    <PageContainer>
      <Header title='Inventory Audit Trail' count={totalRows} hideCount={false}>
        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginBottom: '16px' }}>
          {hasEdits && (
            <div style={{ display: 'flex', gap: '10px' }}>
              <SubmitButton
                onClick={handleSaveChanges}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
              >
                <span>Save Changes</span>
                <div
                  style={{
                    fontSize: '10px',
                    backgroundColor: 'white',
                    color: COLORS.teal,
                    borderRadius: '50%',
                    width: '20px',
                    height: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 'bold',
                  }}
                >
                  {Object.keys(editedCells).length}
                </div>
              </SubmitButton>
              <CancelButton onClick={handleCancelChanges}>Cancel</CancelButton>
            </div>
          )}
        </div>

        <DateRangePicker startDate={startDate} endDate={endDate} setStartDate={setStartDate} setEndDate={setEndDate}/>
 
      </Header>
    
      <DataTable
        data={displayedReports}
        columns={columns}
        title='Inventory Audit Trail'
        pageKey="audit-report"
        loading={loading}
        serverSidePagination={true}
        page={page}
        idField='ID'
        rowCount={totalRows}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        initialPageSize={pageSize}
        hideColumnFilters={true}
        hideSearchBar={true}
        serverSideSort={true}
        onSortModelChange={handleSortChange}
      >
        <LocationSelect
          ovs={ovs}
          selectedOvs={selectedOvs}
          onChange={handleLocationChange}
          setDefault={true}
        />
      </DataTable>
    </PageContainer>
  );
};

export default TransactionAuditReport;